import * as React from "react";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import "../../../styles/areaContainer.css"
import {Box, Center, Grid, HStack, IconButton, Spinner, Text, Tooltip} from "@chakra-ui/react";
import {FiMap} from "react-icons/fi";
import AuthButton from "../../auth/AuthButton";
import {ZoneContextWrapper} from "../../components/context/ZoneContextWrapper";
import {useLazyQuery, useMutation, useQuery} from "@apollo/client";
import {getSpotsQuery} from "../../components/spots/graphQl";
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import {useSearchParams} from "react-router-dom";
import {AreaTools} from "./AreaTools";
import {getAreaContentQuery, getAreaStatisticsQuery, getOrganizationsQuery, refreshAreaStatisticsMutation} from "../../components/area/areaQueries";
import {versionsQuery} from "../versions/versionQueries";
import {getMyAreaEditorSettingsQuery, setMyAreaEditorSettingsMutation} from "../../components/user/userQueries";
import {
    Area,
    AREA_GROUP_MODE_USER_CREATED,
    AreaZoneTemplate,
    AreaZones,
    AreaStatistics,
    BoundariesZone,
    LatLong,
    MarkerSpot,
    SoundFileLibraryEntry,
    SoundFile,
    Spot,
    SpotType,
    Version,
    Zone
} from "../../components/types";
import {MapContext, MapContextI} from "../../components/context/MapContextWrapper";
import AreaDetails from "../areaDetails/AreaDetails";
import {AreaTitle} from "./AreaTitle";
import HelpLink from "../../components/help/HelpLink";
import SoundFileLibraryManager from "../../components/soundfiles/SoundFileLibraryManager";
import AreaSettingsManager from "../../components/area/AreaSettingsManager";
import TranslationsManager from "../../components/translations/TranslationsManager";
import SpaceCreationWizard, {WizardChoices} from "../../components/area/SpaceCreationWizard";
import {compareVersions} from "../../components/common/compareVersions";
import {normalizeAreaGroupModes} from "../../components/area/settings/areaSettingsUtils";
import CueListPanel from "../cueList/CueListPanel";
import {registerSoundFileMutation} from "../../components/soundfiles/soundFileQueries";
import {UserContext, UserContextI} from "../../UserContext";
import {useUploadQueueContext} from "../../components/uploadQueue/UploadQueueContext";
import {v4 as uuidv4} from "uuid";
import {soundFileLibraryQuery} from "../../components/soundfiles/soundFileQueries";
import {ExistingSoundOption} from "../../components/soundfiles/SoundFileLibraryDialog";
import SoundFileDrawer, {
    getSoundLibraryDragPayload,
    hasSoundLibraryDragType,
    SoundLibraryDragPayload
} from "../../components/soundfiles/SoundFileDrawer";
import SpotListView from "./SpotListView";
import {
    getDropTargetExitHandler,
    getDroppedFiles,
    stripFileExtension,
    getDroppedMarkerPosition,
    getNextSpotName,
    hasDroppedFiles,
    PANEL_SPOTS
} from "./areaPanelHelpers";

const BOUNDARIES_ZONE_NAME = "Boundaries";
const CUE_LIST_FEATURE = "CUE_LIST";
const EXT_LANG_FEATURE = "EXT_LANG";
const EMPTY_SPOTS: Spot[] = [];
type OrganizationFeatureInfo = {
    id?: string;
    appVersion?: string;
    features?: string[];
};

const isBoundariesAmbientZone = (zone?: Zone) => {
    const name = (zone?.name || zone?.label || "").trim().toLowerCase();
    return name === BOUNDARIES_ZONE_NAME.toLowerCase();
};

const toLegacyBoundariesZone = (zone: Zone, existingBoundaries?: BoundariesZone): BoundariesZone => {
    return {
        ...zone,
        id: "boundaries",
        name: BOUNDARIES_ZONE_NAME,
        label: BOUNDARIES_ZONE_NAME,
        triggerToActivate: existingBoundaries?.triggerToActivate ?? (zone as BoundariesZone).triggerToActivate ?? false
    } as BoundariesZone;
};

const toBoundariesAmbientZone = (boundariesZone: BoundariesZone): Zone => {
    return {
        ...boundariesZone,
        name: BOUNDARIES_ZONE_NAME,
        label: BOUNDARIES_ZONE_NAME
    } as Zone;
};

const migrateZonesForBoundariesSetting = (zones: AreaZones | undefined, useBoundariesZone: boolean): AreaZones | undefined => {
    if (!zones) {
        return zones;
    }

    let ambientZones: Zone[] = Array.isArray(zones.ambientZones) ? [...zones.ambientZones] : [];

    if (useBoundariesZone) {
        const ambientBoundariesIndex = ambientZones.findIndex((zone) => isBoundariesAmbientZone(zone));
        if (zones.boundaries) {
            if (ambientBoundariesIndex > -1) {
                ambientZones = ambientZones.filter((zone) => !isBoundariesAmbientZone(zone));
            }
            return {
                ...zones,
                ambientZones: ambientZones
            };
        }

        if (ambientBoundariesIndex > -1) {
            const ambientBoundaries = ambientZones[ambientBoundariesIndex];
            ambientZones.splice(ambientBoundariesIndex, 1);
            return {
                ...zones,
                boundaries: toLegacyBoundariesZone(ambientBoundaries, zones.boundaries),
                ambientZones: ambientZones
            };
        }

        return {
            ...zones,
            ambientZones: ambientZones
        };
    }

    if (!zones.boundaries) {
        return {
            ...zones,
            ambientZones: ambientZones
        };
    }

    const filteredAmbientZones = ambientZones.filter((zone) => !isBoundariesAmbientZone(zone));
    const boundariesAsAmbient = toBoundariesAmbientZone(zones.boundaries);
    const remainingZones: AreaZones = {
        ...zones
    };
    delete remainingZones.boundaries;

    return {
        ...remainingZones,
        ambientZones: [...filteredAmbientZones, boundariesAsAmbient]
    };
};

const normalizeAreaZones = (zones: AreaZones | undefined, useBoundariesZone: boolean): AreaZones | undefined => {
    if (!zones) {
        return zones;
    }
    const migratedZones = migrateZonesForBoundariesSetting(zones, useBoundariesZone);
    if (!migratedZones) {
        return migratedZones;
    }
    return {
        ...migratedZones,
        entry: migratedZones.entry || {
            ...AreaZoneTemplate,
            id: "entry",
            name: "Entry"
        },
        maintenance: migratedZones.maintenance || {
            ...AreaZoneTemplate,
            id: "maintenance",
            name: "Maintenance",
            resetApps: false
        },
        ambientZones: Array.isArray(migratedZones.ambientZones) ? [...migratedZones.ambientZones] : []
    };
};

const AreaContainer = () => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
    const userContext: UserContextI = useContext<UserContextI>(UserContext);
    const setDisplayedVersionRef = useRef(appContext.setDisplayedVersion);
    const setContextAreaRef = useRef(appContext.setArea);
    const setContextSpotsRef = useRef(appContext.setSpots);
    setDisplayedVersionRef.current = appContext.setDisplayedVersion;
    setContextAreaRef.current = appContext.setArea;
    setContextSpotsRef.current = appContext.setSpots;
    const splitContainerRef = useRef<HTMLDivElement | null>(null);
    const dividerWidth = 6;
    const [leftWidth, setLeftWidth] = useState<number>(950);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [originalUserSelect, setOriginalUserSelect] = useState<string | undefined>(undefined);
    const [isMapVisible, setIsMapVisible] = useState<boolean>(true);
    const [areaOrganizationId, setAreaOrganizationId] = useState<string | undefined>(undefined);
    const [isCueListEnabled, setIsCueListEnabled] = useState<boolean>(false);
    const [isExtendedLanguagesEnabled, setIsExtendedLanguagesEnabled] = useState<boolean>(false);
    const [isMapDropTarget, setIsMapDropTarget] = useState<boolean>(false);
    const [isSoundDrawerOpen, setIsSoundDrawerOpen] = useState<boolean>(false);
    const statisticsRefreshAreaIdRef = useRef<string | undefined>(undefined);
    const hadActiveUploadsRef = useRef<boolean>(false);
    const defaultRunOnes = !appContext.isOnDemand();
    const {
        uploads,
        enqueueUploads,
        clearCompletedUploads
    } = useUploadQueueContext();
    const [autoCloseTimeoutId, setAutoCloseTimeoutId] = useState<ReturnType<typeof setTimeout> | undefined>(undefined);
    const [registerSoundFile] = useMutation(registerSoundFileMutation);
    const [loadSoundFileLibrary, {data: soundFileData, loading: soundLibraryLoading}] = useLazyQuery(soundFileLibraryQuery, {
        fetchPolicy: "network-only"
    });

    const [search, setSearch] = useSearchParams();
    const areaID = search.get("areaID");
    const shouldShowCreationWizard = search.get("wizard") === "1";

    const activePanel = mapContext.getActivePanel();
    useEffect(() => {
        if (!activePanel) {
            mapContext.setActivePanel(PANEL_SPOTS);
        }
    });

    const {data: versionData} = useQuery(versionsQuery, {
        variables: {areaId: areaID}
    });

    const [loadArea, {loading: loadingArea, data: areaData}] = useLazyQuery(getAreaContentQuery);
    const [loadOrganizations, {data: organizationsData}] = useLazyQuery(getOrganizationsQuery);
    const [loadMapVisibility, {data: mapVisibilityData}] = useLazyQuery(getMyAreaEditorSettingsQuery);
    const [loadAreaStatistics, {data: areaStatisticsData}] = useLazyQuery(getAreaStatisticsQuery, {
        fetchPolicy: "network-only"
    });
    const [setMapVisibility] = useMutation(setMyAreaEditorSettingsMutation);
    const [refreshAreaStatistics] = useMutation(refreshAreaStatisticsMutation);

    const [loadSpots, {loading: loadingSpots, data: spotData}] = useLazyQuery(getSpotsQuery);

    const area = appContext.getArea();
    const currentAreaId = area?.id || areaID || undefined;
    const isSaveStatusIdle = appContext.isSaveStatusIdle();
    const isSaveStatusSaving = appContext.isSaveStatusSaving();
    const currentAreaIdRef = useRef<string | undefined>(currentAreaId);
    currentAreaIdRef.current = currentAreaId;
    const refreshAreaStatisticsInBackground = useCallback(async () => {
        const latestAreaId = currentAreaIdRef.current;
        if (!latestAreaId) {
            console.info("[area-stats:editor] refresh skipped", {
                areaId: latestAreaId
            });
            return;
        }
        try {
            console.info("[area-stats:editor] refresh request", {
                areaId: latestAreaId
            });
            await refreshAreaStatistics({
                variables: {
                    areaId: latestAreaId
                }
            });
            await loadAreaStatistics({
                variables: {
                    areaIds: [latestAreaId]
                }
            });
        } catch (error) {
            console.warn("Unable to refresh area statistics", error);
        }
    }, [loadAreaStatistics, refreshAreaStatistics]);

    useEffect(() => {
        const versions: Version[] = versionData?.getVersions || [];
        if (versions.length === 0) {
            return;
        }
        // Try to find a draft first
        let displayVersion = versions.find((version: Version) => version.publishedAt === 0);

        // Else get the latest released
        if (!displayVersion) {
            displayVersion = versions[0];
        }
        if (displayVersion) {
            setDisplayedVersionRef.current({...displayVersion});
        }
    }, [versionData]);

    const displayedPublishedAt = appContext.getDisplayedVersion()?.publishedAt;

    useEffect(() => {
        if (!areaID || displayedPublishedAt === undefined || displayedPublishedAt === null) {
            return;
        }
        loadArea({
            variables: {
                areaId: areaID,
                publishedAt: displayedPublishedAt
            }
        });
        loadSpots({
            variables: {
                areaId: areaID,
                publishedAt: displayedPublishedAt
            }
        });
        loadMapVisibility({
            variables: {
                areaId: areaID
            }
        });
    }, [areaID, displayedPublishedAt, loadArea, loadSpots, loadMapVisibility]);

    useEffect(() => {
        if (!currentAreaId) {
            statisticsRefreshAreaIdRef.current = undefined;
            console.info("[area-stats:editor] load skipped", {
                areaId: currentAreaId
            });
            return;
        }
        if (statisticsRefreshAreaIdRef.current === currentAreaId) {
            console.info("[area-stats:editor] load request", {
                areaId: currentAreaId
            });
            loadAreaStatistics({
                variables: {
                    areaIds: [currentAreaId]
                }
            });
            return;
        }
        statisticsRefreshAreaIdRef.current = currentAreaId;
        refreshAreaStatisticsInBackground().catch(() => null);
    }, [currentAreaId, loadAreaStatistics, refreshAreaStatisticsInBackground]);

    useEffect(() => {
        const entries: AreaStatistics[] = areaStatisticsData?.getAreaStatistics || [];
        console.info("[area-stats:editor] response", {
            requestedAreaId: currentAreaId,
            count: entries.length,
            areaIds: entries.map((entry) => entry.areaId),
            matched: entries.find((entry) => entry.areaId === currentAreaId)
        });
    }, [areaStatisticsData, currentAreaId]);

    const currentAreaStatistics = useMemo(() => {
        const entries: AreaStatistics[] = areaStatisticsData?.getAreaStatistics || [];
        return entries.find((entry) => entry.areaId === currentAreaId);
    }, [areaStatisticsData, currentAreaId]);

    useEffect(() => {
        const settings = mapVisibilityData?.getMyAreaEditorSettings;
        if (!settings || settings.areaId !== areaID) {
            return;
        }
        setIsMapVisible(settings.mapVisible);
    }, [mapVisibilityData, areaID]);

    useEffect(() => {
        const areaContent = areaData?.getAreaContent;
        if (areaContent) {
            const nextOrganizationId = typeof areaContent.organizationId === "string" ? areaContent.organizationId : undefined;
            setAreaOrganizationId(nextOrganizationId);
            if (nextOrganizationId) {
                loadOrganizations({
                    variables: {
                        organizationIds: [nextOrganizationId]
                    }
                });
            } else {
                setIsCueListEnabled(false);
                setIsExtendedLanguagesEnabled(false);
            }
            const useBoundariesZone = areaContent.useBoundariesZone;
            const normalizedZones = normalizeAreaZones(areaContent.zones, useBoundariesZone);
            const nextArea = {
                id: areaContent.areaId,
                name: areaContent.name,
                organizationId: nextOrganizationId,
                public: areaContent.public === true,
                publicAccessCode: areaContent.publicAccessCode,
                summary: areaContent.summary,
                description: areaContent.description,
                coverImageUrl: areaContent.coverImageUrl,
                creator: areaContent.creator,
                position: areaContent.position && typeof areaContent.position.lat === "number" && typeof areaContent.position.lng === "number"
                    ? {lat: areaContent.position.lat, lng: areaContent.position.lng}
                    : undefined,
                altSyncServerAddress: areaContent.altSyncServerAddress,
                zones: normalizedZones,
                key: areaContent.key,
                modifiedAt: areaContent.modifiedAt,
                status: areaContent.status,
                hasUnpublishedChanges: areaContent.hasUnpublishedChanges,
                languages: areaContent.languages,
                showMap: areaContent.showMap,
                developerMode: areaContent.developerMode,
                showSoundInfo: areaContent.showSoundInfo,
                showTransport: areaContent.showTransport,
                defaultVolume: areaContent.defaultVolume,
                setDefaultVolumeOnReset: areaContent.setDefaultVolumeOnReset,
                appOverridesSystemVolume: areaContent.appOverridesSystemVolume,
                disableVolumeButtons: areaContent.disableVolumeButtons,
                bleScanMode: areaContent.bleScanMode,
                spotTriggerMode: areaContent.spotTriggerMode,
                autoPlay: typeof areaContent.autoPlay === "boolean" ? areaContent.autoPlay : undefined,
                discoverMode: areaContent.discoverMode === "playing" || areaContent.discoverMode === "triggerable" || areaContent.discoverMode === "all"
                    ? areaContent.discoverMode
                    : undefined,
                groupMode: normalizeAreaGroupModes(areaContent.groupMode),
                groupModeAutoWindow: typeof areaContent.groupModeAutoWindow === "number" && Number.isFinite(areaContent.groupModeAutoWindow) && areaContent.groupModeAutoWindow > 0
                    ? areaContent.groupModeAutoWindow
                    : 30,
                useBoundariesZone: useBoundariesZone,
                audioMode: areaContent.audioMode === "stream" || areaContent.audioMode === "download" ? areaContent.audioMode : undefined,
                flowMode: areaContent.flowMode === "sequential" ? "sequential" : "spatial",
                iosCompatible: typeof areaContent.iosCompatible === "boolean" ? areaContent.iosCompatible : undefined,
                downloadFrom: typeof areaContent.downloadFrom === "string" && areaContent.downloadFrom ? areaContent.downloadFrom : undefined,
                downloadTo: typeof areaContent.downloadTo === "string" && areaContent.downloadTo ? areaContent.downloadTo : undefined,
                playFrom: typeof areaContent.playFrom === "string" && areaContent.playFrom ? areaContent.playFrom : undefined,
                playTo: typeof areaContent.playTo === "string" && areaContent.playTo ? areaContent.playTo : undefined
            } as Area;
            console.info("[area-load] getAreaContent", {
                areaId: nextArea.id,
                modifiedAt: nextArea.modifiedAt,
                name: nextArea.name,
                bleScanMode: nextArea.bleScanMode,
                groupMode: nextArea.groupMode
            });
            setContextAreaRef.current(nextArea);
        }
    }, [areaData, loadOrganizations]);

    useEffect(() => {
        if (!areaOrganizationId) {
            setIsCueListEnabled(false);
            setIsExtendedLanguagesEnabled(false);
            return;
        }
        const organizations: OrganizationFeatureInfo[] = Array.isArray(organizationsData?.getOrganizations) ? organizationsData.getOrganizations : [];
        const currentOrganization = organizations.find((org) => org?.id === areaOrganizationId);
        if (currentOrganization) {
            const features = Array.isArray(currentOrganization?.features) ? currentOrganization.features : [];
            const hasCueListFeature = features.includes(CUE_LIST_FEATURE);
            const hasExtendedLanguagesFeature = features.includes(EXT_LANG_FEATURE);
            setIsCueListEnabled(hasCueListFeature);
            setIsExtendedLanguagesEnabled(hasExtendedLanguagesFeature);
            appContext.setOrganizationAppVersion(currentOrganization.appVersion || undefined);
        }
    }, [areaOrganizationId, appContext, organizationsData]);

    const viewMode: "map" | "cues" = area?.flowMode === "sequential" && isCueListEnabled ? "cues" : "map";

    useEffect(() => {
        if (viewMode === "cues") {
            mapContext.setActiveDrawingMode(undefined);
            mapContext.setDrawingModes([]);
            mapContext.setTriggerSelection(undefined);
            mapContext.setSelectedShapeType(undefined);
            appContext.setSelection(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    useEffect(() => {
        const activeCount = uploads.filter((item) => item.status === "queued" || item.status === "uploading").length;
        if (activeCount > 0) {
            hadActiveUploadsRef.current = true;
        } else if (hadActiveUploadsRef.current) {
            hadActiveUploadsRef.current = false;
            refreshAreaStatisticsInBackground().catch(() => null);
        }
        if (activeCount > 0 || uploads.length === 0) {
            if (autoCloseTimeoutId) {
                clearTimeout(autoCloseTimeoutId);
                setAutoCloseTimeoutId(undefined);
            }
            return;
        }
        if (autoCloseTimeoutId) {
            return;
        }
        const timeoutId = setTimeout(() => {
            clearCompletedUploads();
            setAutoCloseTimeoutId(undefined);
        }, 2000);
        setAutoCloseTimeoutId(timeoutId);
    }, [autoCloseTimeoutId, clearCompletedUploads, refreshAreaStatisticsInBackground, uploads]);

    useEffect(() => {
        return () => {
            if (autoCloseTimeoutId) {
                clearTimeout(autoCloseTimeoutId);
            }
        };
    }, [autoCloseTimeoutId]);

    useEffect(() => {
        const currentAreaId = area?.id || areaID;
        if (!isSoundDrawerOpen || !currentAreaId) {
            return;
        }
        loadSoundFileLibrary({
            variables: {
                areaId: currentAreaId
            }
        });
    }, [area?.id, areaID, isSoundDrawerOpen, loadSoundFileLibrary]);

    const spotsForContext = useMemo(() => {
        return spotData?.getSpots ?? EMPTY_SPOTS;
    }, [spotData]);

    const existingSoundOptions = useMemo<ExistingSoundOption[]>(() => {
        const libraryEntries: SoundFileLibraryEntry[] = soundFileData?.getSoundFileLibrary || [];
        const usageMap = new Map<string, {sound: SoundFile; spotNames: string[]}>();
        spotsForContext.forEach((spot: Spot) => {
            spot.soundFiles?.forEach((sf: SoundFile) => {
                if (!sf.fileName) return;
                const existing = usageMap.get(sf.fileName);
                if (existing) {
                    existing.spotNames.push(spot.name || spot.id);
                } else {
                    usageMap.set(sf.fileName, {sound: sf, spotNames: [spot.name || spot.id]});
                }
            });
        });
        return libraryEntries
            .map((entry) => {
                const fileName = entry.objectId || "";
                const usage = usageMap.get(fileName);
                const sound: SoundFile = usage?.sound || {
                    originalFileName: entry.originalFileName,
                    fileName,
                    filePath: entry.filePath,
                    playOnes: true,
                    fadeIn: 0,
                    fadeOut: 0,
                    delay: 0,
                    synchronize: false,
                    noSyncTime: 0,
                    volumeDb: 0
                };
                return {
                    sound,
                    spotNames: usage?.spotNames || [],
                    libraryEntry: entry
                };
            })
            .sort((left, right) => (left.sound.originalFileName || "").localeCompare(right.sound.originalFileName || ""));
    }, [soundFileData, spotsForContext]);

    useEffect(() => {
        setContextSpotsRef.current(spotsForContext);
    }, [spotsForContext]);

    useEffect(() => {
        if (!splitContainerRef.current) return;
        const rect = splitContainerRef.current.getBoundingClientRect();
        const usableWidth = rect.width - dividerWidth;
        const minLeft = 400;
        const minRight = 300;
        const clamped = Math.max(minLeft, Math.min(usableWidth - minRight, leftWidth));
        setLeftWidth(clamped);
    }, [leftWidth]);

    useEffect(() => {
        if (!isDragging) return;
        const onMouseMove = (event: MouseEvent) => {
            if (!splitContainerRef.current) return;
            const rect = splitContainerRef.current.getBoundingClientRect();
            const usableWidth = rect.width - dividerWidth;
            const offsetX = event.clientX - rect.left;
            const minLeft = 300;
            const minRight = 300;
            const clampedLeft = Math.max(minLeft, Math.min(usableWidth - minRight, offsetX));
            setLeftWidth(clampedLeft);
        };
        const onMouseUp = () => {
            setIsDragging(false);
            if (originalUserSelect !== undefined) {
                document.body.style.userSelect = originalUserSelect;
                setOriginalUserSelect(undefined);
            }
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [isDragging, originalUserSelect]);

    const isAreaMatch = !!area && area.id === areaID;

    const toggleMapVisibility = () => {
        const nextVisible = !isMapVisible;
        setIsMapVisible(nextVisible);
        if (!areaID) {
            return;
        }
        setMapVisibility({
            variables: {
                areaId: areaID,
                mapVisible: nextVisible
            }
        }).catch((error) => {
            console.error(error);
        });
    };

    const registerUploadedSound = useCallback(async (uploaded: {
        originalFileName: string;
        fileName: string;
        filePath: string;
        metadata: {
            mimeType?: string;
            sizeBytes?: number;
            durationMs?: number;
        }
    }) => {
        const areaId = appContext.getArea()?.id || areaID;
        if (!areaId) {
            return;
        }
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        try {
            await registerSoundFile({
                variables: {
                    areaId,
                    objectId: uploaded.fileName,
                    originalFileName: uploaded.originalFileName,
                    filePath: uploaded.filePath,
                    uploadedBy,
                    mimeType: uploaded.metadata.mimeType,
                    sizeBytes: uploaded.metadata.sizeBytes,
                    durationMs: uploaded.metadata.durationMs
                }
            });
        } catch (error) {
            console.warn("Unable to register sound file", error);
        }
    }, [appContext, areaID, registerSoundFile, userContext]);

    const assignUploadedFileToSpot = useCallback(async (spot: Spot, uploaded: {
        originalFileName: string;
        fileName: string;
        filePath: string;
        metadata: {
            mimeType?: string;
            sizeBytes?: number;
            durationMs?: number;
        }
    }) => {
        const liveSpot = appContext.getSpot(spot.id) || spot;
        const currentSound = liveSpot.soundFiles?.[0];
        const nextSound: SoundFile = {
            ...currentSound,
            synchronize: currentSound?.synchronize ?? false,
            originalFileName: uploaded.originalFileName,
            fileName: uploaded.fileName,
            filePath: uploaded.filePath,
            delay: currentSound?.delay ?? 0,
            fadeIn: currentSound?.fadeIn ?? 0,
            fadeOut: currentSound?.fadeOut ?? 0,
            volumeDb: currentSound?.volumeDb ?? 0
        };
        appContext.updateSpot(liveSpot, {
            ...liveSpot,
            soundFiles: [nextSound]
        });
        await registerUploadedSound(uploaded);
    }, [appContext, registerUploadedSound]);

    const assignUploadedFileToAmbientZone = useCallback(async (zone: Zone, uploaded: {
        originalFileName: string;
        fileName: string;
        filePath: string;
        metadata: {
            mimeType?: string;
            sizeBytes?: number;
            durationMs?: number;
        }
    }) => {
        const latestArea = appContext.getArea() || area;
        const latestZone = latestArea?.zones?.ambientZones?.find((item) => item.id === zone.id) || zone;
        const currentSound = latestZone.soundFiles?.ambient;
        const nextAmbientSound: SoundFile = {
            ...currentSound,
            synchronize: currentSound?.synchronize ?? false,
            originalFileName: uploaded.originalFileName,
            fileName: uploaded.fileName,
            filePath: uploaded.filePath,
            delay: currentSound?.delay ?? 0,
            fadeIn: currentSound?.fadeIn ?? 0,
            fadeOut: currentSound?.fadeOut ?? 0,
            volumeDb: currentSound?.volumeDb ?? 0
        };
        appContext.updateZone(latestZone, {
            ...latestZone,
            soundFiles: {
                ...latestZone.soundFiles,
                ambient: nextAmbientSound
            }
        });
        await registerUploadedSound(uploaded);
    }, [appContext, area, registerUploadedSound]);

    const assignExistingSoundToSpot = useCallback((spot: Spot, selectedSound: SoundLibraryDragPayload) => {
        const liveSpot = appContext.getSpot(spot.id) || spot;
        const currentSound = liveSpot.soundFiles?.[0];
        if (!selectedSound.fileName) {
            return;
        }
        const nextSound: SoundFile = {
            ...currentSound,
            synchronize: currentSound?.synchronize ?? false,
            originalFileName: selectedSound.originalFileName || selectedSound.fileName,
            fileName: selectedSound.fileName,
            filePath: selectedSound.filePath || "SoundFiles",
            delay: currentSound?.delay ?? 0,
            fadeIn: currentSound?.fadeIn ?? 0,
            fadeOut: currentSound?.fadeOut ?? 0,
            volumeDb: currentSound?.volumeDb ?? 0
        };
        appContext.updateSpot(liveSpot, {
            ...liveSpot,
            soundFiles: [nextSound]
        });
    }, [appContext]);

    const createSpotFromExistingSound = useCallback(async (selectedSound: SoundLibraryDragPayload, position?: LatLong) => {
        if (!appContext.isDraft() || !selectedSound.fileName) {
            return;
        }
        const areaId = appContext.getArea()?.id;
        if (!areaId) {
            return;
        }
        const currentSpots = appContext.getSpots() || [];
        const newSpot: MarkerSpot = {
            id: "",
            area: areaId,
            name: stripFileExtension(selectedSound.originalFileName || selectedSound.fileName),
            type: SpotType.Marker,
            priority: 2,
            beaconSensitivity: 8,
            pauseOtherAmbients: false,
            duckOtherAmbients: true,
            duckOtherAmbientsDb: 0,
            followMainSound: false,
            playMode: "play",
            playFullAfter: 0,
            loop: false,
            runOnes: defaultRunOnes,
            modifiedAt: 0,
            key: "",
            position: position || getDroppedMarkerPosition(currentSpots, mapContext.getMapCenter())
        };
        const createdSpot = await appContext.createSpot(newSpot, {select: true});
        if (!createdSpot) {
            return;
        }
        assignExistingSoundToSpot(createdSpot, selectedSound);
    }, [appContext, assignExistingSoundToSpot, defaultRunOnes, mapContext]);

    const createAmbientZoneFromExistingSound = useCallback((selectedSound: SoundLibraryDragPayload) => {
        if (!appContext.isDraft() || !selectedSound.fileName) {
            return;
        }
        const latestArea = appContext.getArea() || area;
        if (!latestArea) {
            return;
        }
        const existingAmbientZones = Array.isArray(latestArea.zones?.ambientZones)
            ? latestArea.zones.ambientZones
            : [];
        const newZone = {
            ...AreaZoneTemplate,
            id: uuidv4(),
            name: selectedSound.originalFileName || selectedSound.fileName,
            label: selectedSound.originalFileName || selectedSound.fileName,
            soundFiles: {
                ...AreaZoneTemplate.soundFiles,
                ambient: {
                    originalFileName: selectedSound.originalFileName || selectedSound.fileName,
                    fileName: selectedSound.fileName,
                    filePath: selectedSound.filePath || "SoundFiles",
                    synchronize: false,
                    delay: 0,
                    fadeIn: 0,
                    fadeOut: 0,
                    volumeDb: 0
                }
            }
        } as Zone;
        const nextArea = {
            ...latestArea,
            zones: {
                ...latestArea.zones,
                ambientZones: [...existingAmbientZones, newZone]
            }
        };
        setContextAreaRef.current(nextArea);
        appContext.updateArea(latestArea, nextArea);
    }, [appContext, area]);

    const createSpotsFromDroppedFiles = useCallback(async (files: File[], basePosition?: LatLong) => {
        if (!appContext.isDraft() || files.length === 0) {
            return;
        }
        const areaId = appContext.getArea()?.id;
        if (!areaId) {
            return;
        }

        for (let index = 0; index < files.length; index++) {
            const file = files[index];
            const currentSpots = appContext.getSpots() || [];
            const newSpot: MarkerSpot = {
                id: "",
                area: areaId,
                name: stripFileExtension(file.name),
                type: SpotType.Marker,
                priority: 2,
                beaconSensitivity: 8,
                pauseOtherAmbients: false,
                duckOtherAmbients: true,
            duckOtherAmbientsDb: 0,
                followMainSound: false,
                playMode: "play",
                playFullAfter: 0,
                loop: false,
                runOnes: defaultRunOnes,
                modifiedAt: 0,
                key: "",
                position: getDroppedMarkerPosition(currentSpots, basePosition || mapContext.getMapCenter(), index)
            };
            const createdSpot = await appContext.createSpot(newSpot, {select: index === files.length - 1});
            if (!createdSpot) {
                continue;
            }
            enqueueUploads([file], async (uploaded) => {
                await assignUploadedFileToSpot(createdSpot, uploaded);
            });
        }
    }, [appContext, assignUploadedFileToSpot, defaultRunOnes, enqueueUploads, mapContext]);

    const createAmbientZonesFromDroppedFiles = useCallback((files: File[]) => {
        if (!appContext.isDraft() || files.length === 0) {
            return;
        }
        const latestArea = appContext.getArea() || area;
        if (!latestArea) {
            return;
        }
        const existingAmbientZones = Array.isArray(latestArea.zones?.ambientZones)
            ? latestArea.zones.ambientZones
            : [];
        const newZones = files.map((file) => ({
            ...AreaZoneTemplate,
            id: uuidv4(),
            name: file.name,
            label: file.name,
            soundFiles: {
                ...AreaZoneTemplate.soundFiles
            }
        } as Zone));
        const nextArea = {
            ...latestArea,
            zones: {
                ...latestArea.zones,
                ambientZones: [...existingAmbientZones, ...newZones]
            }
        };
        setContextAreaRef.current(nextArea);
        appContext.updateArea(latestArea, nextArea);
        newZones.forEach((zone, index) => {
            enqueueUploads([files[index]], async (uploaded) => {
                await assignUploadedFileToAmbientZone(zone, uploaded);
            });
        });
    }, [appContext, area, assignUploadedFileToAmbientZone, enqueueUploads]);

    const handleSoundDrawerDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "none";
        setIsMapDropTarget(false);
    }, []);

    const handleSoundDrawerDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsMapDropTarget(false);
    }, []);

    const handleMapDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (!hasDroppedFiles(event.dataTransfer) && !hasSoundLibraryDragType(event.dataTransfer)) {
            setIsMapDropTarget(false);
            return;
        }
        event.preventDefault();
        const canDrop = appContext.isDraft();
        event.dataTransfer.dropEffect = canDrop ? "copy" : "none";
        setIsMapDropTarget(canDrop);
    }, [appContext]);

    const handleMapDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>, position?: LatLong) => {
        event.preventDefault();
        setIsMapDropTarget(false);
        const existingSound = getSoundLibraryDragPayload(event.dataTransfer);
        if (existingSound) {
            await createSpotFromExistingSound(existingSound, position);
            return;
        }
        const files = getDroppedFiles(event.dataTransfer);
        await createSpotsFromDroppedFiles(files, position);
    }, [createSpotFromExistingSound, createSpotsFromDroppedFiles]);

    return loadingArea || loadingSpots || !isAreaMatch
        ? (
            <Center width={"100%"} height={"100vh"}>
                <Spinner size={"xl"} thickness={"3px"} color={"blue.500"}/>
            </Center>
        )
        : <Grid width={"inherit"} id={"Grid"} gridTemplateRows={"50px 1fr 40px"} height={"100vh"} minHeight={"100vh"}>
            <Grid gridTemplateColumns={"max-content minmax(0, 1fr) 1fr max-content"} alignItems={"center"} columnGap={3} paddingX={2}>
                <AreaTitle/>
                <AreaTools statistics={currentAreaStatistics}/>
                <div></div>
                <HStack spacing={1} align={"center"}>
                    {!isSaveStatusIdle ? (
                        <Text
                            fontSize={"sm"}
                            fontWeight={"semibold"}
                            color={isSaveStatusSaving ? "orange.500" : "green.500"}
                            marginRight={2}
                        >
                            {isSaveStatusSaving ? "Saving" : "Saved"}
                        </Text>
                    ) : null}
                    <Tooltip label={isMapVisible ? "Hide map" : "Show map"} hasArrow>
                        <IconButton
                            aria-label={isMapVisible ? "Hide map" : "Show map"}
                            icon={<FiMap/>}
                            size={"sm"}
                            variant={isMapVisible ? "solid" : "ghost"}
                            onClick={toggleMapVisibility}
                        />
                    </Tooltip>
                    <SoundFileLibraryManager
                        onAfterUpload={() => {
                            const currentAreaId = area?.id || areaID;
                            if (currentAreaId) {
                                loadSoundFileLibrary({
                                    variables: {
                                        areaId: currentAreaId
                                    }
                                });
                            }
                        }}
                    />
                    <AreaSettingsManager
                        canUseExtendedLanguages={isExtendedLanguagesEnabled}
                        canUseSequentialFlow={isCueListEnabled}
                    />
                    <TranslationsManager/>
                    {area ? (
                        <SpaceCreationWizard
                            isOpen={shouldShowCreationWizard}
                            canUseSequentialFlow={isCueListEnabled}
                            canUseStreaming={compareVersions(appContext.getOrganizationAppVersion() || "3.15", "6.1") >= 0}
                            onSkip={() => {
                                search.delete("wizard");
                                setSearch(search, {replace: true});
                            }}
                            onComplete={async (choices: WizardChoices) => {
                                const nextGroupMode: typeof area.groupMode = choices.useGroups ? [AREA_GROUP_MODE_USER_CREATED] : [];
                                const nextArea = {
                                    ...area,
                                    flowMode: choices.flowMode,
                                    audioMode: choices.audioMode,
                                    autoPlay: choices.autoPlay,
                                    spotTriggerMode: choices.autoPlay ? "automatic" as const : "onDemand" as const,
                                    groupMode: nextGroupMode
                                };
                                appContext.updateArea(area, nextArea);
                                search.delete("wizard");
                                setSearch(search, {replace: true});
                            }}
                        />
                    ) : null}
                    <AuthButton/>
                    <HelpLink path={"/getting-started/"} label={"Open documentation"}/>
                </HStack>
            </Grid>


            <Grid border={"1px solid"} id={"F1"} height={"100%"} minHeight={0}>
                <ZoneContextWrapper>
                    <Grid
                        gridTemplateColumns={isMapVisible ? `${leftWidth}px ${dividerWidth}px 1fr` : "1fr"}
                        height={"100%"}
                        minHeight={0}
                        ref={splitContainerRef}
                        alignItems={"stretch"}
                    >
                        <div className={"areaContainer"} style={{height: "100%"}}>
                            <Box flex={1} minHeight={0} overflow={"hidden"} display={"flex"} flexDirection={"row"}>
                                <Box
                                    width={isSoundDrawerOpen ? "60%" : "100%"}
                                    minWidth={0}
                                    height={"100%"}
                                    overflowY={"auto"}
                                    transition={"width 0.25s ease"}
                                >
                                    {viewMode === "cues" ? (
                                        <CueListPanel
                                            isSoundDrawerOpen={isSoundDrawerOpen}
                                            onToggleSoundDrawer={() => setIsSoundDrawerOpen((previous) => !previous)}
                                            existingSoundOptions={existingSoundOptions}
                                            isLibraryLoading={soundLibraryLoading}
                                            onRequestLibrary={() => {
                                                const targetAreaId = area?.id || areaID;
                                                if (targetAreaId) {
                                                    loadSoundFileLibrary({variables: {areaId: targetAreaId}});
                                                }
                                            }}
                                        />
                                    ) : (
                                        <SpotListView
                                            isSoundDrawerOpen={isSoundDrawerOpen}
                                            onToggleSoundDrawer={() => setIsSoundDrawerOpen((previous) => !previous)}
                                            createSpotFromExistingSound={createSpotFromExistingSound}
                                            createSpotsFromDroppedFiles={createSpotsFromDroppedFiles}
                                            createAmbientZoneFromExistingSound={createAmbientZoneFromExistingSound}
                                            createAmbientZonesFromDroppedFiles={createAmbientZonesFromDroppedFiles}
                                        />
                                    )}
                                </Box>
                                    <SoundFileDrawer
                                        isOpen={isSoundDrawerOpen}
                                        existingSoundOptions={existingSoundOptions}
                                        isLoading={soundLibraryLoading}
                                        onDragOver={handleSoundDrawerDragOver}
                                        onDrop={handleSoundDrawerDrop}
                                        openWidth={"40%"}
                                    />
                                </Box>
                            </div>
                            {isMapVisible ? (
                                <>
                                    <Box
                                        width={`${dividerWidth}px`}
                                        cursor={"col-resize"}
                                        backgroundColor={"#e2e8f0"}
                                        onMouseDown={() => {
                                            setIsDragging(true);
                                            if (originalUserSelect === undefined) {
                                                setOriginalUserSelect(document.body.style.userSelect);
                                            }
                                            document.body.style.userSelect = "none";
                                        }}
                                    />
                                    <Box height={"100%"} minHeight={0}>
                                        <AreaDetails
                                            isSoundDropTarget={isMapDropTarget}
                                            onSoundDragOver={handleMapDragOver}
                                            onSoundDragLeave={getDropTargetExitHandler(setIsMapDropTarget)}
                                            onSoundDrop={handleMapDrop}
                                        />
                                    </Box>
                                </>
                            ) : null}
                    </Grid>
                </ZoneContextWrapper>
            </Grid>
            <Box/>
        </Grid>

};

export default AreaContainer;
